/**
 * ContractTimeline - Timeline visual de eventos do contrato
 * Exibe histórico de mudanças de status em formato cronológico
 * 
 * RELEASE: v1.0.0 - 2026-02-16
 */
import React from 'react';
import {
  FileText, Send, CheckCircle2, XCircle, Clock, AlertCircle,
  FileSignature, PenLine, Ban, Loader2,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('ContractTimeline');

// Props do componente
interface ContractTimelineProps {
  /** ID do contrato */
  contractId: string;
}

/** Mapeamento de status para ícone e cor */
const STATUS_MAP: Record<string, { icon: typeof FileText; color: string; label: string }> = {
  // Status de contrato
  draft: { icon: FileText, color: 'text-muted-foreground', label: 'Rascunho criado' },
  generated: { icon: FileText, color: 'text-blue-500', label: 'PDF gerado' },
  sent: { icon: Send, color: 'text-amber-500', label: 'Enviado para assinatura' },
  signed: { icon: CheckCircle2, color: 'text-green-500', label: 'Contrato assinado' },
  expired: { icon: Clock, color: 'text-red-500', label: 'Contrato expirado' },
  cancelled: { icon: Ban, color: 'text-destructive', label: 'Contrato cancelado' },
  // Status intermediários de processamento
  queued_pdf: { icon: Clock, color: 'text-amber-500', label: 'PDF na fila' },
  processing_pdf: { icon: Loader2, color: 'text-blue-500', label: 'Gerando PDF...' },
  queued_signature: { icon: Clock, color: 'text-amber-500', label: 'Assinatura na fila' },
  processing_signature: { icon: Loader2, color: 'text-blue-500', label: 'Enviando para assinatura...' },
  error: { icon: AlertCircle, color: 'text-destructive', label: 'Erro no processamento' },
  // Status de assinatura
  signature_pending: { icon: PenLine, color: 'text-amber-500', label: 'Aguardando assinatura' },
  signature_signed: { icon: CheckCircle2, color: 'text-green-500', label: 'Assinou o contrato' },
  signature_refused: { icon: XCircle, color: 'text-red-500', label: 'Recusou assinatura' },
  signature_expired: { icon: Clock, color: 'text-muted-foreground', label: 'Assinatura expirada' },
};

/** Label para a origem da mudança */
const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  webhook: 'Autenticadora',
  system: 'Sistema',
};

/**
 * Componente de timeline visual para eventos do contrato
 */
export function ContractTimeline({ contractId }: ContractTimelineProps) {
  // Busca histórico de status do contrato
  const { data: events, isLoading } = useQuery({
    queryKey: ['contract-timeline', contractId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contract_status_history')
        .select('*')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: true });

      if (error) {
        logger.error('Erro ao buscar timeline do contrato', error);
        throw error;
      }

      return data;
    },
    enabled: !!contractId,
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-6 w-6 rounded-full shrink-0" />
            <div className="space-y-1 flex-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Sem eventos
  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center py-4 text-muted-foreground">
        <AlertCircle className="h-6 w-6 opacity-40 mb-1" />
        <p className="text-xs">Nenhum evento registrado</p>
      </div>
    );
  }

  return (
    <div className="relative space-y-0">
      {/* Linha vertical da timeline */}
      <div className="absolute left-[11px] top-3 bottom-3 w-px bg-border" />

      {events.map((event, index) => {
        const statusKey = event.new_status || 'draft';
        const statusInfo = STATUS_MAP[statusKey] || STATUS_MAP.draft;
        const Icon = statusInfo.icon;
        const details = event.details as Record<string, unknown> | null;
        const isLast = index === events.length - 1;

        // Label customizada baseada no detalhe do evento
        let label = statusInfo.label;
        if (details?.event === 'contract_created') {
          label = 'Contrato criado';
        }
        if (details?.event === 'signature_status_change' && details?.signer_name) {
          label = `${details.signer_name} - ${statusInfo.label}`;
        }

        return (
          <div key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
            {/* Ícone do evento */}
            <div className={cn(
              'relative z-10 flex items-center justify-center h-6 w-6 rounded-full shrink-0',
              isLast ? 'bg-primary/10 ring-2 ring-primary/20' : 'bg-muted'
            )}>
              <Icon className={cn('h-3 w-3', statusInfo.color)} />
            </div>

            {/* Conteúdo do evento */}
            <div className="flex-1 min-w-0 pt-0.5">
              <p className={cn(
                'text-xs font-medium',
                isLast ? 'text-foreground' : 'text-muted-foreground'
              )}>
                {label}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-muted-foreground">
                  {format(new Date(event.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
                {event.change_source && event.change_source !== 'system' && (
                  <span className="text-[10px] text-muted-foreground/60">
                    via {SOURCE_LABELS[event.change_source] || event.change_source}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
